import { useState, useEffect } from 'react';

import { useAuth } from '@frontend/components/AuthProvider';
import { Layout } from '@frontend/components/Layout';
import { useTranslation } from '@frontend/i18n';
import { Box, Button, Typography, Select, MenuItem, Stack, Paper, Alert, TextField, RadioGroup, FormControlLabel, Radio, FormControl, FormLabel, IconButton, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';

interface MemberWithCredits {
  id: number;
  name: string;
  email: string;
  role: 'admin' | 'member';
  credits: number;
  provider?: string;
  providerId?: string;
  enabled?: boolean;
  avatarUrl?: string;
  passwordHash?: string;
  lastLogin?: string;
  createdAt?: string;
  createdBy?: number;
  updatedAt?: string;
  updatedBy?: number;
}

export default function Team(): JSX.Element {
  const { user, activeOrganization, activeRole } = useAuth();
  const [members, setMembers] = useState<MemberWithCredits[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [editingCredits, setEditingCredits] = useState<{ userId: number; currentCredits: number } | null>(null);
  const [newCreditsValue, setNewCreditsValue] = useState<string>('');
  const [inviteCode, setInviteCode] = useState<string | null>(null);
  const [generatingInvite, setGeneratingInvite] = useState(false);
  const [inviteType, setInviteType] = useState<'oauth' | 'password'>('oauth');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteCredentials, setInviteCredentials] = useState<{ email: string; password: string } | null>(null);
  const t = useTranslation();

  // Calculate user permissions
  const isAdmin = activeRole === 'admin';
  const canGenerateInvites = isAdmin;

  useEffect(() => {
    const loadMembers = async () => {
      if (!activeOrganization) return;

      try {
        // Fetch members with credits
        const response = await fetch(`/api/v1/credits/users`);
        if (!response.ok) {
          throw new Error(await response.text());
        }
        const data = (await response.json()) as { users: Array<{ id: number; name: string; email: string; credits: number; role: string }> };
        setMembers(data.users.map(u => ({
          id: u.id,
          name: u.name,
          email: u.email,
          role: u.role as 'admin' | 'member',
          credits: u.credits,
        })));
      } catch (err) {
        console.error('Failed to load members:', err);
        setError(t.team.failedToLoadMembers);
      } finally {
        setLoading(false);
      }
    };

    loadMembers();
  }, [activeOrganization, t.team.failedToLoadMembers]);

  const handleGenerateInvite = async (multiUse: boolean = false) => {
    console.log('Generating invite code, multiUse:', multiUse, 'type:', inviteType);
    if (!activeOrganization) return;

    if (inviteType === 'password' && !inviteEmail) {
      setError('Email is required for password invites');
      return;
    }

    setGeneratingInvite(true);
    setError(null);
    setInviteCredentials(null);

    try {
      const response = await fetch(`/api/v1/organizations/invites`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          multiUse,
          inviteType,
          email: inviteType === 'password' ? inviteEmail : undefined,
        }),
      });

      if (!response.ok) {
        throw new Error(await response.text());
      }

      const data = (await response.json()) as { code: string; email?: string; tempPassword?: string };
      setInviteCode(data.code);
      
      if (inviteType === 'password' && data.email && data.tempPassword) {
        setInviteCredentials({ email: data.email, password: data.tempPassword });
      }
    } catch (err) {
      console.error('Failed to generate invite:', err);
      setError(t.team.failedToGenerateInvite);
    } finally {
      setGeneratingInvite(false);
    }
  };

  const handleUpdateRole = async (userId: number, newRole: 'admin' | 'member') => {
    if (!activeOrganization) return;

    try {
      const response = await fetch(`/api/v1/organizations/members/${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role: newRole }),
      });

      if (!response.ok) {
        throw new Error(await response.text());
      }

      // Reload members after role update
      const membersResponse = await fetch(`/api/v1/credits/users`);
      if (!membersResponse.ok) {
        throw new Error(await membersResponse.text());
      }
      const data = (await membersResponse.json()) as { users: Array<{ id: number; name: string; email: string; credits: number; role: string }> };
      setMembers(data.users.map(u => ({
        id: u.id,
        name: u.name,
        email: u.email,
        role: u.role as 'admin' | 'member',
        credits: u.credits,
      })));
    } catch (err) {
      console.error('Failed to update role:', err);
      setError(t.team.failedToUpdateRole);
    }
  };

  const handleOpenCreditsDialog = (userId: number, currentCredits: number) => {
    setEditingCredits({ userId, currentCredits });
    setNewCreditsValue(currentCredits.toString());
  };

  const handleCloseCreditsDialog = () => {
    setEditingCredits(null);
    setNewCreditsValue('');
  };

  const handleSetCredits = async () => {
    if (!editingCredits) return;

    const credits = parseFloat(newCreditsValue);
    if (isNaN(credits) || credits < 0) {
      setError('Please enter a valid positive number');
      return;
    }

    try {
      const response = await fetch(`/api/v1/credits/users/${editingCredits.userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ credits }),
      });

      if (!response.ok) {
        throw new Error(await response.text());
      }

      // Reload members after credits update
      const membersResponse = await fetch(`/api/v1/credits/users`);
      if (!membersResponse.ok) {
        throw new Error(await membersResponse.text());
      }
      const data = (await membersResponse.json()) as { users: Array<{ id: number; name: string; email: string; credits: number; role: string }> };
      setMembers(data.users.map(u => ({
        id: u.id,
        name: u.name,
        email: u.email,
        role: u.role as 'admin' | 'member',
        credits: u.credits,
      })));

      handleCloseCreditsDialog();
    } catch (err) {
      console.error('Failed to set credits:', err);
      setError('Failed to set credits');
    }
  };

  const handleRemoveMember = async (userId: number) => {
    if (!activeOrganization) return;

    try {
      const response = await fetch(`/api/v1/organizations/members/${userId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error(await response.text());
      }

      // Reload members after removal
      const membersResponse = await fetch(`/api/v1/organizations/members`);
      if (!membersResponse.ok) {
        throw new Error(await membersResponse.text());
      }
      setMembers(members.filter((m) => m.id !== userId));
    } catch (err) {
      console.error('Failed to remove member:', err);
      setError(t.team.failedToRemoveMember);
    }
  };

  // Show loading state only when we don't have user data yet
  // Show loading state only while fetching initial session data
  if (loading) {
    console.log('Showing loading state...');
    return (
      <Layout title="Team">
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <Typography>{t.team.loading}</Typography>
        </Box>
      </Layout>
    );
  }

  // Safety check - we should have both user and activeOrg
  if (!activeOrganization?.id) {
    console.log('No active organization');
    return <></>;
  }

  return (
    <Layout title={t.team.title}>
      <Box>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {canGenerateInvites && (
          <Paper sx={{ mb: 3, p: 3 }}>
            <Typography variant="h5" gutterBottom>
              {t.team.inviteMembers}
            </Typography>
            <Box>
              <FormControl component="fieldset" sx={{ mb: 2 }}>
                <FormLabel component="legend">Invite Type</FormLabel>
                <RadioGroup
                  row
                  value={inviteType}
                  onChange={(e) => {
                    setInviteType(e.target.value as 'oauth' | 'password');
                    setInviteCode(null);
                    setInviteCredentials(null);
                  }}
                >
                  <FormControlLabel value="oauth" control={<Radio />} label="OAuth (Google/GitHub)" />
                  <FormControlLabel value="password" control={<Radio />} label="Username + Password" />
                </RadioGroup>
              </FormControl>

              {inviteType === 'password' && (
                <TextField
                  fullWidth
                  label="Email Address"
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  sx={{ mb: 2 }}
                  placeholder="user@example.com"
                />
              )}

              <Stack direction="row" spacing={2}>
                <Button variant="contained" onClick={() => handleGenerateInvite(false)} disabled={generatingInvite}>
                  {t.team.generateSingleUseInvite}
                </Button>
                <Button variant="outlined" onClick={() => handleGenerateInvite(true)} disabled={generatingInvite}>
                  {t.team.generateMultiUseInvite}
                </Button>
              </Stack>

              {inviteCode && (
                <Paper sx={{ mt: 2, p: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="caption" color="text.secondary">
                    Invite Code:
                  </Typography>
                  <Typography variant="body1" fontFamily="monospace" sx={{ mb: 1 }}>
                    {inviteCode}
                  </Typography>
                  {inviteCredentials && (
                    <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Login Credentials (share these with the user):
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        <strong>Email:</strong> {inviteCredentials.email}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Password:</strong> {inviteCredentials.password}
                      </Typography>
                      <Alert severity="warning" sx={{ mt: 1 }}>
                        Password cannot be changed. Save these credentials securely.
                      </Alert>
                    </Box>
                  )}
                </Paper>
              )}
            </Box>
          </Paper>
        )}

        <Paper>
          <Box sx={{ px: 2, py: 2.5 }}>
            <Typography variant="h5" gutterBottom>
              {t.team.organizationMembers}
            </Typography>
            <Box>
              {members.map((member) => (
                <Box
                  key={member.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    py: 1.5,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    '&:last-child': { borderBottom: 0 },
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="center" sx={{ flex: 1 }}>
                    <Box sx={{ minWidth: 200 }}>
                      <Typography variant="subtitle2">{member.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {member.email}
                      </Typography>
                    </Box>
                    <Box sx={{ minWidth: 120 }}>
                      {isAdmin && member.id !== user?.id ? (
                        <Select
                          value={member.role}
                          onChange={(e) => handleUpdateRole(member.id!, e.target.value as 'admin' | 'member')}
                          size="small"
                        >
                          <MenuItem value="member">{t.team.member}</MenuItem>
                          <MenuItem value="admin">{t.team.admin}</MenuItem>
                        </Select>
                      ) : (
                        <Paper
                          variant="outlined"
                          sx={{
                            px: 1.5,
                            py: 0.75,
                            bgcolor: 'grey.100',
                            typography: 'body2',
                          }}
                        >
                          {member.role.charAt(0).toUpperCase() + member.role.slice(1)}
                        </Paper>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 150 }}>
                      <Typography variant="body2" fontWeight={600}>
                        ${member.credits.toFixed(2)}
                      </Typography>
                      {isAdmin && (
                        <IconButton
                          size="small"
                          onClick={() => handleOpenCreditsDialog(member.id!, member.credits)}
                          sx={{ ml: 1 }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  </Stack>
                  {isAdmin && member.id !== user?.id && (
                    <Button onClick={() => handleRemoveMember(member.id!)} size="small" color="error" variant="text">
                      {t.team.remove}
                    </Button>
                  )}
                </Box>
              ))}
            </Box>
          </Box>
        </Paper>

        {/* Credits Edit Dialog */}
        <Dialog open={!!editingCredits} onClose={handleCloseCreditsDialog}>
          <DialogTitle>Set Credits</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Credits"
              type="number"
              fullWidth
              value={newCreditsValue}
              onChange={(e) => setNewCreditsValue(e.target.value)}
              inputProps={{ min: 0, step: 0.01 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseCreditsDialog}>Cancel</Button>
            <Button onClick={handleSetCredits} variant="contained">Set Credits</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Layout>
  );
}
